// src/components/Dashboard.js - Operational Dashboard
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useEmployees } from "../hooks/useEmployees";
import { isEmployeeActiveToday } from "../utils/employeeFilters";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../services/dashboardService";
import { getOtApprovalSummary } from "../services/otApprovalSummaryService";
import { getPayrollApprovalSummary } from "../services/payrollApprovalSummaryService";
import { fetchPendingAdjustmentSummary } from "../services/adjustmentService";
import {
  DASHBOARD_PULSE_STATS,
  DASHBOARD_CARDS,
} from "../config/dashboardCards";
import "../styles/sections/overview.css";

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, company, hasPermission } = useAuth();
  const canViewPayroll = hasPermission('payroll:view');
  const canProcessPayroll = hasPermission('payroll:run');
  const canSeeOT = hasPermission('time:ot:view');
  const canApprovePayroll = hasPermission('payroll:approve');
  const canViewAdjustments = hasPermission('adj:view');
  const { employees, stats, loading: employeesLoading } = useEmployees(company?.id);

  // Dashboard-specific data
  const [timeRecordsSummary, setTimeRecordsSummary] = useState(null);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [otSummary, setOtSummary] = useState(null);
  const [payrollApprovalSummary, setPayrollApprovalSummary] = useState(null);
  const [adjustmentsSummary, setAdjustmentsSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data on mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!company?.id) return;

      setLoading(true);
      try {
        // Gate each fetch on the caller's permission. Without the gate,
        // low-privilege custom roles (e.g. DTR encoder lacking time:view
        // and payroll:view) trigger permission-denied errors in the console.
        // Match the resolved value to the shape dashboardService returns
        // so downstream destructuring stays valid.
        const promises = [
          hasPermission("time:view")
            ? dashboardService.getTodayTimeRecordsSummary(company.id)
            : Promise.resolve({
                employeeIdsWithRecords: new Set(),
                recordCount: 0,
                date: "",
              }),
          hasPermission("payroll:view")
            ? dashboardService.getCompletedPayrollRuns(company.id, 5)
            : Promise.resolve([]),
        ];
        const otIndex = canSeeOT ? promises.length : -1;
        if (canSeeOT) {
          promises.push(getOtApprovalSummary(company.id));
        }
        const payrollApprovalIndex = canApprovePayroll ? promises.length : -1;
        if (canApprovePayroll) {
          promises.push(getPayrollApprovalSummary(company.id));
        }
        const adjustmentsIndex = canViewAdjustments ? promises.length : -1;
        if (canViewAdjustments) {
          // Isolated: a rejection here would otherwise reject the whole
          // Promise.all and blank every other card (the catch below only logs).
          // Resolving to null instead leaves this one card hidden — the
          // `!= null` guard and the card's own null guard both handle it.
          promises.push(fetchPendingAdjustmentSummary(company.id).catch(() => null));
        }

        const results = await Promise.all(promises);

        setTimeRecordsSummary(results[0]);
        setPayrollRuns(results[1]);
        if (otIndex >= 0 && results[otIndex]) {
          setOtSummary(results[otIndex]);
        }
        if (payrollApprovalIndex >= 0 && results[payrollApprovalIndex]) {
          setPayrollApprovalSummary(results[payrollApprovalIndex]);
        }
        // Explicit null check, not truthiness: null means "not loaded / failed"
        // and must stay distinct from a legitimate summary of zero pending
        // entries, which still has to render the card's empty state.
        if (adjustmentsIndex >= 0 && results[adjustmentsIndex] != null) {
          setAdjustmentsSummary(results[adjustmentsIndex]);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [company?.id, canSeeOT, canApprovePayroll, canViewAdjustments, hasPermission]);

  // Derived data
  const today = new Date();
  const lastPayroll = payrollRuns.length > 0 ? payrollRuns[0] : null;

  // Filter active employees for calculations — employment dates only, via the
  // shared predicate (BL-075). This previously ALSO rejected on status.current,
  // which could disagree with the dates until the nightly batch caught up; the
  // dates are the source of truth, so the dashboard now matches the employee list.
  const activeEmployees = useMemo(
    () => employees.filter(isEmployeeActiveToday),
    [employees]
  );

  // Calculate time records stats
  const timeRecordsStats = useMemo(() => {
    const withRecords = timeRecordsSummary?.recordCount || 0;
    const onLeave = stats.onLeave || 0;
    const missingDTR = Math.max(0, stats.active - withRecords - onLeave);

    return { withRecords, missingDTR, onLeave };
  }, [timeRecordsSummary, stats]);

  // Calculate department breakdown
  const departmentBreakdown = useMemo(() => {
    return dashboardService.calculateDepartmentBreakdown(activeEmployees);
  }, [activeEmployees]);

  // Calculate estimated gross
  const estimatedGross = useMemo(() => {
    return dashboardService.calculateEstimatedGross(activeEmployees);
  }, [activeEmployees]);

  // Generate donut chart gradient
  const donutGradient = useMemo(() => {
    const departments = Object.entries(departmentBreakdown);
    if (departments.length === 0) return "conic-gradient(var(--ui-border) 0deg 360deg)";

    let currentAngle = 0;
    const segments = departments.map(([, data]) => {
      const startAngle = currentAngle;
      const endAngle = currentAngle + (data.percentage / 100) * 360;
      currentAngle = endAngle;
      return `${data.color} ${startAngle}deg ${endAngle}deg`;
    });

    return `conic-gradient(${segments.join(", ")})`;
  }, [departmentBreakdown]);

  // Loading state
  if (!user || !company) {
    return (
      <div className="loading">
        <div className="loading__spinner--ph"></div>
        <div className="loading__text">Loading dashboard...</div>
      </div>
    );
  }

  const isLoading = loading || employeesLoading;

  // Filter pulse stats and cards by caller permissions. Config-driven —
  // see src/config/dashboardCards.js for what's gated by what.
  const visiblePulseStats = DASHBOARD_PULSE_STATS.filter(
    (s) => !s.requires || hasPermission(s.requires)
  );
  const visibleCards = DASHBOARD_CARDS.filter((c) =>
    hasPermission(c.requires)
  );

  // Props bag spread into every card — each component reads what it needs.
  const cardProps = {
    isLoading,
    today,
    timeRecordsStats,
    activeEmployees,
    departmentBreakdown,
    donutGradient,
    lastPayroll,
    otSummary,
    payrollApprovalSummary,
    adjustmentsSummary,
    canProcessPayroll,
    navigate,
    company,
  };

  // Props bag for pulse-stat getValue / getLabel.
  const pulseProps = { today, isLoading, stats, estimatedGross };

  return (
    <div className="dashboard">
      {/* TODAY'S PULSE BANNER — config-driven; each stat is gated by its
          `requires` permission in DASHBOARD_PULSE_STATS. */}
      {visiblePulseStats.length > 0 && (
        <div className="pulse-banner">
          <div className="pulse-banner__decoration"></div>
          <div className="pulse-banner__title">Today's Pulse</div>
          <div className="pulse-stats">
            {visiblePulseStats.map((stat) => (
              <div key={stat.id} className="pulse-stat">
                <div
                  className={`pulse-stat__value${
                    stat.valueClass ? ` ${stat.valueClass}` : ""
                  }`}
                >
                  {stat.getValue(pulseProps)}
                </div>
                <div className="pulse-stat__label">
                  {stat.getLabel ? stat.getLabel(pulseProps) : stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THREE-COLUMN GRID — config-driven; each card gated by its
          `requires` permission in DASHBOARD_CARDS. */}
      {visibleCards.length > 0 && (
        <div className="dashboard-grid">
          {visibleCards.map(({ id, Component }) => (
            <Component key={id} {...cardProps} />
          ))}
        </div>
      )}

      {/* PAYROLL HISTORY TABLE */}
      {canViewPayroll && <div className="dashboard-card dashboard-card--full">
        <div className="dashboard-card__header">
          <span className="dashboard-card__title">Payroll History</span>
          {payrollRuns.length > 0 && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/company/${company.slug}/payroll`);
              }}
              className="dashboard-card__link"
            >
              View all →
            </a>
          )}
        </div>
        {isLoading ? (
          <div className="dashboard-card__loading">Loading payroll history...</div>
        ) : payrollRuns.length > 0 ? (
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Employees</th>
                <th>Gross</th>
                <th>Gov't Contributions</th>
                <th>Loans</th>
                <th>Adjustments</th>
                <th>Net</th>
                <th>Processed Date</th>
              </tr>
            </thead>
            <tbody>
              {payrollRuns.map((run) => (
                <tr key={run.id}>
                  <td className="payroll-table__period">
                    {dashboardService.formatPeriod(run.periodStart, run.periodEnd)}
                  </td>
                  <td>{run.summary.totalEmployees}</td>
                  <td className="payroll-table__amount payroll-table__amount--gross">
                    {dashboardService.formatCurrency(run.summary.totalGross)}
                  </td>
                  <td className="payroll-table__amount payroll-table__amount--deductions">
                    {dashboardService.formatCurrency(run.summary.totalDeductions)}
                  </td>
                  <td className="payroll-table__amount payroll-table__amount--deductions">
                    {/* PRD_LoansManagement C8i — absent on parents written before loans existed */}
                    {dashboardService.formatCurrency(run.summary.totalLoans || 0)}
                  </td>
                  <td className={`payroll-table__amount ${(run.summary.totalAdjustments || 0) >= 0 ? 'payroll-table__amount--adjustments-positive' : 'payroll-table__amount--adjustments-negative'}`}>
                    {(run.summary.totalAdjustments || 0) >= 0 ? '+' : ''}{dashboardService.formatCurrency(run.summary.totalAdjustments || 0)}
                  </td>
                  <td className="payroll-table__amount payroll-table__amount--net">
                    {dashboardService.formatCurrency(run.summary.totalNet)}
                  </td>
                  <td className="payroll-table__date">
                    {run.lastModified
                      ? dashboardService.formatDateWithYear(run.lastModified)
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-state__text">
              No payroll history yet. Your completed payroll runs will appear here.
            </div>
          </div>
        )}
      </div>}

      {/* DEPARTMENT PAYROLL BREAKDOWN */}
      {canViewPayroll && lastPayroll && Object.keys(lastPayroll.summary.byDepartment || {}).length > 0 && (
        <div className="dashboard-card dashboard-card--full">
          <div className="dashboard-card__header">
            <span className="dashboard-card__title">Department Payroll Breakdown</span>
            <span className="dashboard-card__subtitle">Last Processed Period</span>
          </div>
          <div className="breakdown-list">
            {Object.entries(lastPayroll.summary.byDepartment).map(([dept, data]) => {
              const totalGross = lastPayroll.summary.totalGross || 1;
              const percentage = Math.round((data.gross / totalGross) * 100);
              const color = departmentBreakdown[dept]?.color || "var(--ui-text-faint)";

              return (
                <div key={dept} className="breakdown-item">
                  <div className="breakdown-item__dept">
                    {dept} <span>({data.employees})</span>
                  </div>
                  <div className="breakdown-item__amount">
                    {dashboardService.formatCurrency(data.gross)}
                  </div>
                  <div className="breakdown-bar">
                    <div
                      className="breakdown-bar__fill"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: color
                      }}
                    ></div>
                  </div>
                  <div className="breakdown-item__percent">{percentage}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
